import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Lightning, MusicNotes, PaperPlaneTilt, ImageSquare,
  MusicNote, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-400">
        {label}{required && <span className="text-neon-magenta ml-1">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[10px] text-zinc-600 mt-1 font-mono">{hint}</span>}
    </label>
  );
}

function FileDrop({ accept, onSelect, file, kind }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const Icon = kind === "image" ? ImageSquare : MusicNote;
  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) onSelect(f);
      }}
      className={`rounded-lg border-2 border-dashed cursor-pointer p-5 transition-colors text-center ${drag ? "border-neon-cyan bg-neon-cyan/10" : "border-ink-600 bg-ink-900/60 hover:border-neon-cyan/60"}`}
      data-testid={`submit-${kind}-drop`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onSelect(f); }}
        className="hidden"
        data-testid={`submit-${kind}-input`}
      />
      {file ? (
        <div className="flex items-center gap-2 justify-center text-sm">
          <Icon size={16} className="text-neon-cyan" />
          <span className="text-white truncate">{file.name}</span>
          <span className="text-xs text-zinc-500 font-mono">{(file.size / (1024 * 1024)).toFixed(1)} MB</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1.5 text-zinc-500 text-sm">
          <Icon size={20} className="text-neon-cyan" />
          <span>Drop {kind} or click to pick</span>
          <span className="text-[10px] font-mono">{kind === "image" ? "jpg / png / webp" : "mp3 / wav / m4a"}</span>
        </div>
      )}
    </div>
  );
}

const inputCls = "mt-1.5 w-full px-3 py-2 rounded-md bg-ink-900 border border-ink-600 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-neon-cyan/60";

export default function ArtistSubmit() {
  const [form, setForm] = useState({
    artist_name: "", song_title: "", contact_email: "", bio: "",
    instagram: "", twitter: "", youtube: "", spotify: "",
    soundcloud: "", tiktok: "", website: "", video_url: "",
  });
  const [audio, setAudio] = useState(null);
  const [image, setImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.artist_name.trim() || !form.song_title.trim() || !form.contact_email.trim()) {
      toast.error("Artist name, song title and contact email are required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (audio) fd.append("audio", audio, audio.name);
      if (image) fd.append("image", image, image.name);
      const { data } = await api.post("/submissions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.ok) {
        setDone(true);
        toast.success("Submission received");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-900 text-white" data-testid="artist-submit-page">
      <header className="sticky top-0 z-30 glass border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-neon-cyan transition-colors" data-testid="submit-back-link">
            <ArrowLeft size={16} weight="bold" />
            <span className="font-mono text-xs uppercase tracking-widest">Back to countdown</span>
          </Link>
          <div className="flex items-center gap-2">
            <Lightning size={18} weight="fill" className="text-neon-cyan" />
            <span className="font-display font-black tracking-tight text-sm">UNDAGROUND SHOOW</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-3">Get on the countdown</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-3">Submit your heater.</h1>
          <p className="text-sm sm:text-base text-zinc-400 max-w-xl">
            Real artists, real tracks, real shot. Drop your single, your art, and your links — if the streets like it, you land on the next Top 10.
          </p>
        </motion.div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="mt-10 rounded-xl border border-neon-cyan/40 bg-neon-cyan/[0.06] p-8 text-center"
            data-testid="submit-success"
          >
            <CheckCircle size={42} weight="fill" className="text-neon-cyan mx-auto mb-3" />
            <h2 className="text-2xl font-bold tracking-tight mb-2">You&apos;re in the pile.</h2>
            <p className="text-sm text-zinc-300 mb-6">
              The Undaground Shoow team reviews submissions weekly. If you&apos;re picked, you&apos;ll get an email at <span className="text-white font-mono">{form.contact_email}</span>.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button onClick={() => { setDone(false); setForm({ artist_name: "", song_title: "", contact_email: "", bio: "", instagram: "", twitter: "", youtube: "", spotify: "", soundcloud: "", tiktok: "", website: "", video_url: "" }); setAudio(null); setImage(null); }} className="px-4 py-2 rounded-md border border-ink-600 text-zinc-200 hover:text-neon-cyan hover:border-neon-cyan/50 text-sm" data-testid="submit-another">Submit another</button>
              <Link to="/" className="px-4 py-2 rounded-md bg-neon-cyan text-black text-sm font-semibold hover:bg-neon-cyanHover" data-testid="submit-success-back">Back to countdown</Link>
            </div>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-8" data-testid="submit-form">
            {/* Track basics */}
            <section className="rounded-xl border border-ink-600 bg-ink-900/40 p-5 sm:p-6 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <MusicNotes size={18} className="text-neon-cyan" weight="bold" />
                <h3 className="text-sm font-bold tracking-[0.25em] uppercase">The Track</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Artist name" required>
                  <input className={inputCls} value={form.artist_name} onChange={update("artist_name")} placeholder="Almight E" required data-testid="submit-artist-name" />
                </Field>
                <Field label="Song title" required>
                  <input className={inputCls} value={form.song_title} onChange={update("song_title")} placeholder="Arch City Anthem" required data-testid="submit-song-title" />
                </Field>
              </div>
              <Field label="Bio" hint="Short, true — 1-3 sentences max">
                <textarea rows={3} className={inputCls} value={form.bio} onChange={update("bio")} placeholder="Three sentences max. Where you're from, what you make, why this one matters." data-testid="submit-bio" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Audio file" hint="Up to 25MB · 1–2 min snippet works best">
                  <div className="mt-1.5"><FileDrop kind="audio" accept="audio/*" file={audio} onSelect={setAudio} /></div>
                </Field>
                <Field label="Artwork" hint="Square preferred, up to 25MB">
                  <div className="mt-1.5"><FileDrop kind="image" accept="image/*" file={image} onSelect={setImage} /></div>
                </Field>
              </div>
            </section>

            {/* Contact + Socials */}
            <section className="rounded-xl border border-ink-600 bg-ink-900/40 p-5 sm:p-6 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <PaperPlaneTilt size={18} className="text-neon-cyan" weight="bold" />
                <h3 className="text-sm font-bold tracking-[0.25em] uppercase">Reach you</h3>
              </div>
              <Field label="Contact email" required hint="We'll only use this to tell you you're in">
                <input type="email" className={inputCls} value={form.contact_email} onChange={update("contact_email")} placeholder="you@artist.com" required data-testid="submit-email" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Spotify"><input className={inputCls} value={form.spotify} onChange={update("spotify")} placeholder="https://open.spotify.com/artist/…" data-testid="submit-spotify" /></Field>
                <Field label="Instagram"><input className={inputCls} value={form.instagram} onChange={update("instagram")} placeholder="@yourhandle" data-testid="submit-instagram" /></Field>
                <Field label="X (Twitter)"><input className={inputCls} value={form.twitter} onChange={update("twitter")} placeholder="@yourhandle" data-testid="submit-twitter" /></Field>
                <Field label="YouTube"><input className={inputCls} value={form.youtube} onChange={update("youtube")} placeholder="https://youtube.com/@…" data-testid="submit-youtube" /></Field>
                <Field label="SoundCloud"><input className={inputCls} value={form.soundcloud} onChange={update("soundcloud")} placeholder="https://soundcloud.com/…" data-testid="submit-soundcloud" /></Field>
                <Field label="TikTok"><input className={inputCls} value={form.tiktok} onChange={update("tiktok")} placeholder="@yourhandle" data-testid="submit-tiktok" /></Field>
                <Field label="Website"><input className={inputCls} value={form.website} onChange={update("website")} placeholder="https://…" data-testid="submit-website" /></Field>
                <Field label="Music video URL"><input className={inputCls} value={form.video_url} onChange={update("video_url")} placeholder="https://youtu.be/…" data-testid="submit-video" /></Field>
              </div>
            </section>

            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-zinc-500 font-mono max-w-md">
                By submitting, you confirm you own or have the rights to the audio and artwork you upload.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-neon-cyan text-black text-sm font-bold tracking-wide hover:bg-neon-cyanHover disabled:opacity-60"
                data-testid="submit-form-btn"
              >
                <PaperPlaneTilt size={16} weight="fill" />
                {submitting ? "Submitting..." : "Send it in"}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
